import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Company } from '../../../models/company.model';
import { Employee } from '../../../models/employee.model';
import EmployeeDetailsCard from '../../../common/employee-details-card/EmployeeDetailsCard';
import EmploymentCard from '../../../common/employment-card/EmploymentCard';
import QualificationsCard from '../../../common/qualifications-card/QualificationsCard';
import EmergencyContactCard from '../../../common/emergency-contact-card/EmergencyContactCard';
import FormUpload from '../../../common/form-upload/FormUpload';
import { NWClient } from '../../../client/NWClient';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { selectUser } from '../../../slices/user/userSlice';
import { fetchDocuments, selectAllDocuments } from '../../../slices/documents/documentsSlice';
import { RootState } from '../../../store';

const ID_CARDS_WORKFLOW_KEY = 'tagsLabels.documentWorkflow.employment.idCardsPassportsAndLicenses';

interface DetailsTabsProps {
  company: Company;
  employee: Employee;
  key: string;
}

const DetailsTabs: React.FC<DetailsTabsProps> = ({ company, employee }) => {
  const { t } = useTranslation();
  const { employeeId } = useParams();
  const currentUser = useAppSelector(selectUser);
  const workflowLabel = t(ID_CARDS_WORKFLOW_KEY);

  const dispatch = useAppDispatch();
  const documents = useAppSelector(selectAllDocuments);
  const docsStatus = useAppSelector((state) => state.documents.status);
  const docsLoading = docsStatus === 'loading';
  const taskIds = useAppSelector((state: RootState) => state.tasks.taskIds);

  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const fetchIdDocuments = useCallback(() => {
    if (!currentUser?.id) return;
    if (!(taskIds && taskIds.length === 0)) return;
    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          category: 'employment',
          workflow: workflowLabel,
          ...(employeeId ? { profile: Number(employeeId) } : {}),
        },
      })
    );
  }, [dispatch, currentUser?.id, employeeId, workflowLabel, taskIds]);

  useEffect(() => {
    fetchIdDocuments();
  }, [fetchIdDocuments]);

  const handleUploadCancel = () => {
    setIsUploadOpen(false);
    //fetchIdDocuments();
  };

  return (
    <>
      <Grid container wrap='wrap' rowGap={2}>
        <Grid size={{ xs: 12 }}>
          <EmployeeDetailsCard employee={employee} company={company} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <EmploymentCard employee={employee} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <QualificationsCard employee={employee} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <EmergencyContactCard employee={employee} />
        </Grid>

        {/* ID Cards, Passports & Licenses section */}
        <Grid size={{ xs: 12 }}>
          <Card style={{ overflow: 'visible' }}>
            <CardContent>
              <Grid
                container
                alignItems='center'
                paddingBottom='16px'
                minHeight='60px'
                flexWrap='nowrap'
              >
                <Grid size={{ xs: 11 }} container alignItems='center' gap={1}>
                  <Grid sx={{ marginBottom: '0.2rem' }}>
                    <BadgeOutlinedIcon />
                  </Grid>
                  <Grid>
                    <Typography variant='h6'>{workflowLabel}</Typography>
                  </Grid>
                </Grid>
                <Grid
                  size={{ xs: 1 }}
                  style={{ overflow: 'visible' }}
                  container
                  justifyContent='flex-end'
                >
                  <Box sx={{ position: 'relative' }}>
                    <IconButton
                      onClick={() => setIsUploadOpen((prev) => !prev)}
                      title={t('buttons.upload')}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                    {isUploadOpen && (
                      <Box sx={{ position: 'absolute', right: '100%', top: 0, zIndex: 1005 }}>
                        <FormUpload
                          categoryValue='employment'
                          onCancel={handleUploadCancel}
                          employees={employee ? [employee] : []}
                          workflow={workflowLabel}
                          className='show__left__top'
                          accept='image/png, image/jpeg, application/pdf, image/heic'
                          supportedFormatsKey='messages.supportedFormatsImages'
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {docsLoading ? (
                <Box display='flex' justifyContent='center' py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : documents.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  {t('messages.noDocuments', 'No documents uploaded yet.')}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {documents.map((doc) => (
                    <ListItem key={doc.id} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <InsertDriveFileOutlinedIcon fontSize='small' />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant='body2'
                            component='span'
                            onClick={() =>
                              NWClient.downloadDocument(doc.id, doc.name ?? String(doc.id))
                            }
                            sx={{
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              color: 'primary.main',
                            }}
                          >
                            {doc.name ?? String(doc.id)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default DetailsTabs;
